import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OpsSummaryService } from '../../../core/ops/ops-summary.service';
import { OpsOverdueItem } from '../../../core/ops/ops-summary.types';
import { aiRecommendationLabel } from '../../../core/status/status-meta.types';
import { IconComponent } from '../../../shared/icon/icon';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';

const QUEUE_LABELS: Record<string, string> = {
  ks: 'Kickstart Cash',
  df_facilities: 'DF Facilities',
  df_invoices: 'DF Invoices',
};

@Component({
  selector: 'app-ops-breaching',
  imports: [RouterLink, IconComponent, ZarPipe],
  templateUrl: './ops-breaching.html',
  styleUrl: './ops-breaching.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpsBreachingComponent implements OnInit {
  private readonly summaryService = inject(OpsSummaryService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly items = signal<OpsOverdueItem[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  protected readonly count = computed(() => this.items().length);

  protected readonly skeletonRows = [1, 2, 3, 4, 5];

  ngOnInit(): void {
    this.load();
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.items.set([]);
    this.cdr.markForCheck();
    this.load();
  }

  /** Express overdue business-hours as whole business days (9h/day, SAST workday). */
  protected overdueLabel(hours: number): string {
    const days = hours / 9;
    if (days >= 1) {
      const d = Math.round(days * 10) / 10;
      return `${d} business day${d === 1 ? '' : 's'} overdue`;
    }
    return `${Math.round(hours)}h overdue`;
  }

  protected queueLabel(queue: string): string {
    return QUEUE_LABELS[queue] ?? queue;
  }

  protected readonly aiRecLabel = aiRecommendationLabel;

  protected aiRecClass(rec: string): string {
    const map: Record<string, string> = {
      APPROVE: 'ai-rec--approve',
      MORE_INFO: 'ai-rec--more-info',
      DECLINE: 'ai-rec--decline',
    };
    return map[rec] ?? '';
  }

  private load(): void {
    this.summaryService
      .breaching()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.items);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
